import { COHORT_STATUS_COMPLETED } from "@pathwright/features/src/modules/group/constants"
import { getApolloClient } from "@pathwright/features/src/modules/pathwright/PathwrightClient"
import { getIsSubscriptionExpired } from "@pathwright/features/src/modules/subscription/utils"
import { flattenEdges } from "@pathwright/features/src/modules/utils/apollo"
import { getResourceSlugId } from "@pathwright/features/src/modules/utils/urls"
import { UserGroupSubscriptionsQueryDocument } from "@pathwright/graphql-client/src/generated"
import { isNoStatusFetchFailure } from "@pathwright/utils/src/graphql/connectionGate"
import { captureMessage, withScope } from "@sentry/browser"
import get from "lodash/get"
import { getURLParameter } from "../../lib/core/api/urls"
import { isResourceRoute } from "./isResourceRoute"

// TODO: This file was created by bulk-decaffeinate.
// Sanity-check the conversion and remove this comment.
const Resource = require("library/models/resource").default
const RegistrationCollection =
  require("library/models/registration-collection").default
const Offering = require("library/models/offering").default
const OfferingCollection = require("library/models/offering-collection").default
const {
  ENROLLMENT_ROLE_OBSERVER,
  ENROLLMENT_ROLE_STUDENT,
  ENROLLMENT_ROLE_FACILITATOR
} = require("invitation/constants")
const {
  getPaginationData,
  isAbortError,
  isUnloadedError
} = require("lib/core/api/resource-loader")

const OFFERING_ROUTE_RE = /\/library\/[0-9a-zA-Z-_]+\/\d+\//

// Wrapper for store actions to return the action's promise
const actionPromiseExposer =
  (fn) =>
  (...args) =>
    fn(...args).promise

class ResourceStore extends require("lib/static-shim").default(
  require("lib/core/store/backbone-store").default
) {
  static initClass() {
    ;`\
Container for the user's relationship with a resource
All resource controllers and views should use this data
as the "source of truth" for their data.\
` // will overlay screen if expired subscription

    this.prototype.resources = {
      resource: {
        model: Resource
      },
      offering: {
        model: Offering,
        collection: OfferingCollection
      },
      registration: {
        collection: RegistrationCollection
      }
    }

    this.prototype.storeTriggerHandlers = {
      resourceAdmin: {
        "resource:updated"(resource) {
          if (resource.id === this.get("resource")?.id) {
            return this.action.refreshResource()
          }
        },
        "resource:deleted"(resource) {
          if (resource?.id === this.get("resource")?.id) {
            // clear store after route change, Not Before!, otherwise everything breaks
            return this.listenToOnce(
              this.getStore("navigation"),
              "change:url",
              this.action.clearSelectedResource
            )
          }
        }
      },
      authorAdmin: {
        "author:deleted"(deletedAuthor) {
          const resourceAuthors = this.get("resource")?.get("authors")
          if (
            resourceAuthors &&
            resourceAuthors.find((author) => author.id === deletedAuthor.id)
          ) {
            return this.action.refreshResource()
          }
        }
      },
      groupAdmin: {
        "group:updated"(offering) {
          if (offering.id === this.get("offering")?.id) {
            this.get("offering").set(offering.toJSON())
            if (this.get("offerings").get(offering.id)) {
              this.get("offerings").get(offering.id).set(offering.toJSON())
            }
            return this.triggerChange("change:offering")
          }
        },
        // NOTE: this event no longer fires since we don't create new cohorts
        // via the groupAdmin store,but we may want to consider porting this
        // handler over.
        "group:created"(offering) {
          const resource = this.get("resource")
          const offerings = this.get("offerings")
          // Maybe a better solution available, but the resource has been "updated" in a way
          // since the num_offerings has changed at least
          // TODO: something similar for "offering:deleted"?
          if (resource != null) {
            if (offerings != null) {
              // Trying to avoid reloading offerings if we can
              resource.set({
                num_offerings: (resource.get("num_offerings") || 0) + 1
              })
              offerings.add(offering, { at: 1 })
              if (!this.get("offering")) {
                this.action.selectOfferingByID(offering.id)
              }
              return this.triggerChange("change:offerings")
            } else {
              let forceReload, route
              const { slug } = this.getState().resource
              const { section } = this.getState()
              return this.action.selectResourceBySlug(
                slug,
                offering.id,
                section,
                (route = false),
                (forceReload = true)
              )
            }
          }
        },
        "group:deleted"(offering) {
          return this.action.handleDeletedOffering(offering)
        }
      },
      auth: {
        "user:signed:in"() {
          if (!App.getStore("register").getState().state) {
            // need to make sure this sign in didn't happen because of a sign-up
            return this.action.reload(true)
          }
        },
        "user:signed:out"() {
          return this.action.reload(false)
        }
      },
      register: {
        "registration:created"({ offering }) {
          return this.action._onRegistrationCreated(offering)
        }
      },
      member: {
        "removed:member"() {
          const { offering } = this.getState()
          if (offering) {
            this.get("offering").set({
              seats_filled: offering.seats_filled - 1
            })
            return this.triggerChange("change:offering")
          }
        }
      }
    }

    this.prototype.storeTriggers = {
      "offering:changed"() {
        return this.get("offering")
      },
      "resource:reloaded"() {
        return this.getState()
      }
    }

    this.prototype.actions = {
      refreshResource() {
        return this.resource.resource.load(
          { id: this.get("resource").id },
          false,
          true // ignoring cache
        )
      },

      setResource(resource) {
        return this._mutate({ resource })
      },

      selectResource(resource, offering, section) {
        if (offering == null) {
          offering = null
        }
        const changes = {}
        if (resource.id !== this.get("resource")?.id) {
          changes.resource = resource
        }

        if (offering != null && offering.id !== this.get("offering")?.id) {
          if (offering.get("resource_id") === resource.id) {
            changes.offering = offering
          }
        }

        if (!_.isEmpty(changes)) {
          this._mutate(changes)
        }
      },

      setRegistration(registration) {
        // This should allow views to reload properly on registered
        return this._mutate({ registration })
      },

      async selectOfferingByID(
        offeringID,
        forceReload = false,
        ignoreCache = false,
        epoch = null
      ) {
        this._assertResourceLoadEpoch(epoch)
        offeringID = parseInt(offeringID)
        const off = this.get("offering")
        const shouldFetch =
          !off || (off && off.id !== offeringID) || forceReload || ignoreCache
        if (shouldFetch) {
          // Try to find it in the resource
          const resource = this.get("resource")
          if (!resource) throw new Error("No resource selected.")
          const offerings = this.get("offerings")
          let offering = null

          if (offerings && !forceReload && !ignoreCache) {
            offering = offerings.find(function (o) {
              return o.id === offeringID
            })
          }

          if (!offering || forceReload || ignoreCache) {
            const invite_token = getURLParameter("ic") || null
            const params = {
              resource_id: resource.id,
              id: offeringID,
              invite_token
            }
            return await this.resource.offering.load(params, {
              forceReload,
              ignoreCache,
              shouldCommit: () => this._isCurrentResourceLoadEpoch(epoch)
            })
          } else {
            this._assertResourceLoadEpoch(epoch)
            this._mutate({ offering })
            return offering
          }
        } else {
          return this.get("offering")
        }
      },

      selectResourceBySlug(slug, offeringID, section, route, forceReload) {
        // Slug may be either a string slug with ending numeric ID or a numeric ID.
        const maybeSlugId = getResourceSlugId(slug)

        if (route == null) {
          route = false
        }
        if (forceReload == null) {
          forceReload = false
        }
        const loadKey = JSON.stringify([
          maybeSlugId || slug,
          offeringID == null ? null : Number(offeringID),
          section,
          route,
          forceReload
        ])
        const pendingLoad = this._pendingResourceLoad
        // A page and its card can request the same cohort at the same time.
        if (
          pendingLoad?.key === loadKey &&
          this._isCurrentResourceLoadEpoch(pendingLoad.epoch)
        ) {
          return pendingLoad.promise
        }
        let { resource, offering } = this.getState()

        if (resource && resource.slug !== slug && resource.id !== maybeSlugId) {
          this.action.clearSelectedResource()
        }

        if (
          offering != null &&
          offeringID != null &&
          (offering != null ? offering.id : undefined) !== parseInt(offeringID)
        ) {
          this.action.clearSelectedOffering(false)
        }

        // Capture after any clear so this load owns the epoch.
        const epoch = this._beginResourceLoadEpoch()

        this._mutate({ section })

        const promise = this.resource.resource
          // Prefer the slug ID over the slug, this way we can likely cache results
          // more consistently based on the slug ID, avoiding unnecessary reloads.
          .load(
            { slug: maybeSlugId || slug },
            {
              forceReload,
              shouldCommit: () => this._isCurrentResourceLoadEpoch(epoch)
            }
          )
          .then((fetchedResource) => {
            this._assertResourceLoadEpoch(epoch)
            const resourceError = this.get("resource_error")
            if (resourceError) throw resourceError

            resource = fetchedResource
            if (resource?.id) this.action.selectResource(resource)
            if (!resource?.id || !this.get("resource")) {
              console.warn("Resource cleared during load", {
                slug,
                forceReload
              })
              throw new DOMException(
                "Resource cleared during load",
                "AbortError"
              )
            }
            if (offeringID) {
              return this.action
                .selectOfferingByID(offeringID, false, false, epoch)
                .promise.then(
                  actionPromiseExposer(this.action.checkOfferingPermissions)
                )
                .catch((err) => {
                  if (isUnloadedError(err)) throw err
                  this._assertResourceLoadEpoch(epoch)
                  this.action.ensureOffering(epoch)
                })
                .then((offering) => (forceReload = false))
            } else {
              return (forceReload = false)
            }
          })
          .then((forceReload) => {
            this._assertResourceLoadEpoch(epoch)
            return this.action.loadOfferings(forceReload, false, epoch).promise
          })
          .then(() => {
            this._assertResourceLoadEpoch(epoch)
            if (route) {
              this.action.setURL()
            }
            return null
          })
          .then(() => {
            this._assertResourceLoadEpoch(epoch)
            if (this.getStore("auth").request.isAuthenticated()) {
              return this.action.loadUserRegistrations(false, false, epoch)
                .promise
            }
          })
          .then(() => {
            this._assertResourceLoadEpoch(epoch)
            this.action.ensureOffering(epoch)
          })
          .then(() => {
            this._assertResourceLoadEpoch(epoch)
            this.action.updateNavigationTitle(epoch)
          })
          .catch((err) => {
            // A newer selectResourceBySlug aborted this load and owns the UI.
            if (isAbortError(err)) return
            console.error(err)

            if (isNoStatusFetchFailure(err)) {
              App.getStore("layout").action.showIn("popupAlert", null, {
                error: `Unable to load resource.`,
                onClear: () =>
                  App.getStore("layout").action.closeIn("popupAlert")
              })
              return
            }

            const { offering, offering_error, resource_error } = this.getState()
            const { is_authenticated } = window.App.getStore("auth").getState()

            // Slightly handle permissions
            if (!offering || offering_error || resource_error) {
              let nextLocation =
                window.App.getStore("navigation").request.getNextLocation()
              if (is_authenticated) {
                // You can't view this resource. Tap your heals, Dorathy
                nextLocation =
                  window.App.getStore("navigation").request.getBackTo()
              }

              // show popupError
              const error = offering_error || resource_error
              App.getStore("layout").action.showIn("popupAlert", null, {
                error: `Unable to load resource.`,
                description: error?.message,
                onClear: () =>
                  App.getStore("layout").action.closeIn("popupAlert")
              })
              window.App.navigate(nextLocation)
            }
          })
          .finally(() => {
            if (this._pendingResourceLoad?.promise === promise) {
              this._pendingResourceLoad = null
            }
          })
        this._pendingResourceLoad = { key: loadKey, epoch, promise }
        return promise
      },

      ensureOffering(epoch = null) {
        this._assertResourceLoadEpoch(epoch)
        const { offerings, resource } = this.getState()
        let { offering } = this.getState()
        const permissions = window.App.getStore("school").getState()
        const { session } = window.App.getStore("auth").getState()

        if (!resource) return

        if (!offering) {
          if (offerings && offerings.length) {
            offering = new Offering(offerings[0])
          } else if (
            // Staff
            (permissions &&
              (permissions.user_is_school_editor ||
                permissions.user_is_school_admin)) ||
            // Members, if resource can be viewed by members
            (session &&
              session.school_membership &&
              !!session.school_membership.id &&
              resource.visibility_level_override == 10) ||
            // Anyone, if resource can be viewed by anyone
            resource.visibility_level_override == 15
          ) {
            offering = new Offering({
              id: resource.curriculum_offering,
              permissions: {},
              name: resource.name
            })
          }

          if (offering) {
            this._assertResourceLoadEpoch(epoch)
            this._mutate({ offering })
          } else {
            throw new Error(
              "You do not have permissions to view this offering."
            )
          }
        }
      },

      loadOfferings(forceReload = false, ignoreCache = false, epoch = null) {
        this._assertResourceLoadEpoch(epoch)
        const resource = this.get("resource")
        return this.resource.offering.list(
          { resource: resource.id, page_size: 100 },
          {
            forceReload,
            ignoreCache,
            shouldCommit: () => this._isCurrentResourceLoadEpoch(epoch)
          }
        )
      },

      loadMoreOfferings() {
        // TODO: bake this into loaders (if we really need it going forward)
        const key = "offerings"
        return new Promise(
          function (resolve, reject) {
            const collection = this.get(key)
            this._mutate({ [`${key}_loading_more`]: true })
            return collection
              .next()
              .then(() => {
                const pagination_data = getPaginationData(collection)
                this._mutate({
                  [`${key}_loading_more`]: false,
                  [`${key}_pagination`]: pagination_data
                })
                this.triggerChange(`change:${key}`)
                return resolve()
              })
              .fail(reject)
          }.bind(this)
        )
      },

      selectResourceSection(section) {
        return this._mutate({ section })
      },

      reload(signedIn) {
        let offeringID
        const { resource, offering, section } = this.getState()
        if (resource == null) {
          return
        }

        const navStore = this.getStore("navigation")

        // need to clear then reload
        if (signedIn) {
          offeringID = offering != null ? offering.id : undefined
        } else {
          offeringID = null
        }

        // clear the resource cache
        this.resource.resource.clearCache()
        this.resource.offering.clearCache()
        this.resource.registration.clearCache()

        window.App.stores.requestStore("path").reset()
        window.App.stores.requestStore("step").reset()
        this.action.clearSelectedResource()
        // The next visit loads the resource fresh.
        if (!isResourceRoute(resource)) return
        // Only reload if user potentially has perms to access resource.
        if (signedIn || resource.highest_visibility_level >= 15) {
          let forceReload, route
          return this.action
            .selectResourceBySlug(
              resource.slug,
              offeringID,
              section,
              (route = false),
              (forceReload = true)
            )
            .promise.then(() => {
              if (this.get("offering")) {
                return this.storeTrigger("resource:reloaded")
              }
            })
        } else {
          navStore.action.goHome()
        }
      },

      checkOfferingPermissions() {
        const { offering, offering_error } = this.getState()
        if (offering_error && [401, 403].includes(offering_error.status)) {
          throw new Error("You do not have permissions to view this offering.")
        }
      },

      clearSelectedOffering(clearCache = true) {
        window.App.stores.requestStore("path").reset()
        window.App.stores.requestStore("pathAdmin").reset()
        window.App.stores.requestStore("register").reset()
        if (clearCache) {
          this.resource.offering.clearCache()
          this._mutate(this.resource.offering.initialState)
        }
      },

      clearSelectedResource() {
        //if @get("resource")
        // TODO: make this event based?
        this._invalidateResourceLoadEpoch()
        this.action.clearSelectedOffering()
        this.action.clearSelectedRegistration()
        this.reset()
      },

      clearSelectedRegistration() {
        this.resource.registration.clearCache()
        return this._mutate(this.resource.registration.initialState)
      },

      loadUserRegistrations(
        forceReload = false,
        ignoreCache = false,
        epoch = null
      ) {
        this._assertResourceLoadEpoch(epoch)
        const resource = this.get("resource")
        if (!resource) {
          throw new DOMException("Resource cleared during load", "AbortError")
        }
        const params = { resource_id: resource.id }
        return this.resource.registration
          .list(params, {
            forceReload,
            ignoreCache,
            shouldCommit: () => this._isCurrentResourceLoadEpoch(epoch)
          })
          .then(() => {
            this._assertResourceLoadEpoch(epoch)
            return this.action.selectRegistration(epoch).promise
          })
      },

      // Selects a registration and ensures we have the offering matching that registration
      selectRegistration(epoch = null) {
        return new Promise(
          function (resolve, reject) {
            this._assertResourceLoadEpoch(epoch)
            let registration
            const updates = { registration: null }
            const { offering, offerings, registrations } = this.getState()

            if (registrations && registrations.length) {
              // If offering then we may find a matching registration
              registration = registrations.find(
                (r) => r.offering.id === get(offering, "id")
              )
              // Don't select a new offering based on registration if we're we're an admin
              if (
                !this.request.permissions("can_edit_resource") &&
                !OFFERING_ROUTE_RE.test(window.location.pathname)
              ) {
                if (!registration && offerings && offerings.length) {
                  // Either no offering or no registration matching the offering
                  registration = registrations.find((r) =>
                    offerings.find((o) => r.offering.id === o.id)
                  )
                }
                if (!registration) {
                  // No registration matching any offering, need to load the offering
                  return resolve(
                    this.action
                      .selectOfferingByID(
                        registrations[0].offering.id,
                        false,
                        false,
                        epoch
                      )
                      .promise.then((offering) => {
                        this._assertResourceLoadEpoch(epoch)
                        this._mutate({ offering })
                        return this.action.selectRegistration(epoch)
                      })
                  )
                }
              }
            }
            if (registration) {
              updates.registration = this.get("registrations").get(
                registration.id
              )
              // The selected offering didn't match any registration
              if (
                registration.offering.id !==
                  (offering != null ? offering.id : undefined) &&
                !this.request.permissions()?.can_edit_resource
              ) {
                updates.offering = this.get("offerings").get(
                  registration.offering.id
                )
              }
            } else if (!offering) {
              updates.offering = this.get("offerings").first()
            }

            this._assertResourceLoadEpoch(epoch)
            this._mutate(updates)
            return resolve(registration)
          }.bind(this)
        )
      },

      handleDeletedOffering(offering) {
        const selected = this.get("offering")
        const offerings = this.get("offerings")

        // Nothing to update
        if (!offerings || !selected) {
          return
        }

        // Update offerings if they contain the deleted offering.
        if (offerings.get(offering.id)) {
          this.resource.offering.clearCache()
          offerings.remove(offering.id)
          this.triggerChange("change:offerings")
        }

        // Clear selected offering if it was deleted.
        if (selected.id === offering.id) {
          return this.action.clearSelectedOffering()
        }
      },

      updateNavigationTitle(epoch = null) {
        this._assertResourceLoadEpoch(epoch)
        let { id, slug, name: title } = this.getState().resource
        if (!title) {
          title = this.getStore("school").getState().school.name
        }
        return this.getStore("navigation").action.setURLTitle(title)
      },

      async _onRegistrationCreated(offering) {
        const forceReload = false
        const ignoreCache = true
        const queryOptions = [forceReload, ignoreCache]
        // Ignoring cache - mainly to update the staff shown above the path,
        // and ingoreCache doesn't clear the resource keys causing UI to flash (as opposed to forceReload which does)
        await this.action.loadOfferings(...queryOptions).promise
        await this.action.selectOfferingByID(offering.id, ...queryOptions).promise // prettier-ignore
        await this.action.loadUserRegistrations(...queryOptions).promise
      }
    }

    this.prototype.requests = {
      discussionEnabled() {
        const { offering, resource } = this.getState()
        return (
          (resource != null ? resource.discussion_enabled : undefined) &&
          (offering != null ? offering.discussion_enabled : undefined)
        )
      },

      canRegisterForOffering() {
        const { offering, resource } = this.getState()
        if (!offering || !resource) {
          return false
        }
        if (offering.id === resource.curriculum_offering) {
          return false
        }
        return true
      },

      getInviteURLs() {
        const { offering, registration } = this.getState()
        if (
          (registration != null ? registration.invite_urls : undefined) != null
        ) {
          return registration.invite_urls
        } else if (
          (offering != null ? offering.invite_urls : undefined) != null
        ) {
          return offering.invite_urls
        }
        return null
      },

      toRegisterLink() {
        const { resource, offerings } = this.getState()

        let toRegister = ""

        if (resource) {
          toRegister = {
            pathname: `/library/${resource.slug}/register/`,
            state: { modal: true }
          }

          if (offerings?.length === 1 && !resource.license_offerings?.length) {
            toRegister.pathname += `${offerings[0].id}/`
          }
        }

        return toRegister
      },

      getRegistrationForOffering(offeringID) {
        const { registrations } = this.getState()
        return _.find(registrations, (r) => r.offering.id === offeringID)
      },

      curriculumOffering() {
        return this.get("offerings")?.find(
          (offering) => offering.get("is_curriculum") === true
        )
      },

      getOfferingByID(offeringID) {
        if (this.get("offering")?.id === parseInt(offeringID)) {
          return this.get("offering")
        }
        if (this.get("offerings")?.length) {
          return this.get("offerings").get(offeringID)
        }
        return null
      },

      permissions: {
        deps: ["resource", "offering", "registration", "registrations"],
        // Non-arrow so cacheFunction can bind the store instance (arrows use static initClass `this`).
        func(key) {
          const { resource, offering, registration } = this.getState()

          let sourceRegistration = null
          try {
            // It's possible the user may have a source ohort registration.
            // In that case, let's also base perms off that registration.
            sourceRegistration = this.request.getRegistrationForOffering(
              resource?.curriculum_offering
            )
          } catch (error) {
            withScope((scope) => {
              scope.setExtra({
                resourceStoreState: this.getState()
              })
              captureMessage("Failed to get registration for source offering.")
            })
          }

          let perms = null
          // returns the permissions for the current resource/offering
          if (offering) {
            perms = {
              ...offering.permissions
            }
          }

          function assignPermsFromRegistration(registration) {
            if (registration) {
              if (perms == null) {
                perms = {}
              }
              perms = _.extend(
                perms,
                { can_access: true },
                registration.permissions
              )
              perms.can_invite_observer =
                perms.can_invite_observer ||
                registration.invite_urls.observer != null
              perms.can_invite_student =
                perms.can_invite_student ||
                registration.invite_urls.student != null
              perms.can_invite_moderator =
                perms.can_invite_moderator ||
                registration.invite_urls.moderator != null
              perms.can_invite_teacher =
                perms.can_invite_teacher ||
                registration.invite_urls.teacher != null
              perms.can_facilitate =
                perms.can_facilitate ||
                registration.role >= ENROLLMENT_ROLE_FACILITATOR
              if (offering) {
                perms.can_view_analytics =
                  perms.can_view_analytics ||
                  (() => {
                    switch (offering.analytics_visibility) {
                      case 5:
                        return registration.role >= ENROLLMENT_ROLE_FACILITATOR
                      case 10:
                        return registration.role >= ENROLLMENT_ROLE_STUDENT
                      case 15:
                        return true
                      default:
                        return false
                    }
                  })()
              }
            }
          }

          // In order of level of permission, assign perms based on registration
          // then source registration.
          assignPermsFromRegistration(registration)
          assignPermsFromRegistration(sourceRegistration)

          if (perms != null) {
            // Cohort creators can manage the Cohort without being Path designers.
            // Use the legacy fallback only when no Path permission was supplied.
            if (perms.can_edit_path == null) {
              perms.can_edit_path = perms.can_edit
            }
            perms.can_edit = perms.can_edit || perms.can_edit_path
          }

          if (perms != null && perms.can_edit_resource == null) {
            perms.can_edit_resource = perms.can_edit
          }

          if (perms != null && perms.can_edit_offering == null) {
            perms.can_edit_offering = perms.can_edit
          }

          // remove certain perms from user if the offering is "completed" or the registration is archived
          // and the use does not have can_edit_path perms
          if (
            perms &&
            !perms.can_edit_path &&
            (offering?.status === COHORT_STATUS_COMPLETED ||
              registration?.soft_archived_time)
          ) {
            perms.can_invite_observer = false
            perms.can_invite_student = false
            perms.can_invite_moderator = false
            perms.can_invite_teacher = false
            perms.can_facilitate = false
            perms.can_participate = false
            perms.can_register = false
          }

          // Until backend properly locks down can_participate based on these conditions, we'll apply them here.
          // If teh cohort hasn't started yet and the user doesn't have moderator perms, we lock down participation.
          if (
            perms &&
            !perms.can_facilitate &&
            offering?.access_starts &&
            moment(offering.access_starts).isAfter()
          ) {
            perms.can_participate = false
          }

          // Until backend properly provides users with can_edit_resource the can_participate permission, we'll override it here.
          // Otherwise, Course Editors are unable to access Community, Notes, etc.
          if (perms && perms.can_edit_resource) {
            perms.can_participate = true
          }

          if (key) {
            if (perms) {
              return perms[key] || false
            }
            return false
          }

          return perms
        }
      },
      // returns list of subscription plan IDs included in the cohort that the user has access to
      includedInUserSubscription(offering) {
        const { subscription, subscribe_state } =
          this.getStore("subscribe").getState()
        if (
          offering &&
          (subscription != null ? subscription.subscription : undefined) !=
            null &&
          subscribe_state === "active"
        ) {
          return _.intersection(offering.include_in_subscription_plans, [
            subscription.school_plan.id
          ])
        }
        return []
      },

      // returns list of group subscription plan IDs (in the form of Stripe produts) included in the cohort that the user has access to
      async includedInUserGroupSubscription(offering) {
        const { user, is_authenticated: _currentAuthState } =
          App.getStore("auth").getState()
        const userId = user?.id
        const hasAccess = (user?.membership?.membership_type ?? null) !== null
        // force json data
        offering = offering && offering.toJSON ? offering.toJSON() : offering

        if (
          offering &&
          offering.include_with_products &&
          offering.include_with_products.length &&
          hasAccess
        ) {
          // NOTE: schoolGroupSubscriptions are scoped to user's mentor group memberships
          const SCHOOL_GROUP_SUBSCRIPTIONS_QUERY =
            UserGroupSubscriptionsQueryDocument

          const client = getApolloClient()

          const userSchoolGroupSubscriptionsQuery = await client.query({
            query: SCHOOL_GROUP_SUBSCRIPTIONS_QUERY,
            variables: { userId },
            fetchPolicy:
              this._prevAuthState !== _currentAuthState
                ? "network-only"
                : "cache-first"
          })

          const groupSubscriptions = flattenEdges(
            get(
              userSchoolGroupSubscriptionsQuery,
              "data.schoolGroupSubscriptions"
            ) || []
          )

          if (groupSubscriptions.length) {
            const activeGroupSubscriptions = groupSubscriptions.filter(
              (sub) =>
                !getIsSubscriptionExpired(sub.subscription) &&
                sub.community_group.user_membership &&
                !sub.community_group.user_membership.access_revoked_time
            )

            const activeGroupSubscriptionIds = activeGroupSubscriptions.map(
              (sub) => sub.price.product.id
            )

            return _.intersection(
              offering.include_with_products,
              activeGroupSubscriptionIds
            ).map(
              // HACK ALERT: need to map back to the group subscription ID for registration purposes
              (planId) =>
                activeGroupSubscriptions.find(
                  (sub) => sub.price.product.id === planId
                ).id
            )
          }
        }

        this._prevAuthState = _currentAuthState

        return []
      },

      seatsAvailable(offering, invitation) {
        if (!offering) {
          return null
        }
        if (
          offering.resource_license &&
          offering.resource_license.ignore_seat_count
        ) {
          return -1
        }
        if (!offering.resource_license) {
          if (invitation && invitation.role === ENROLLMENT_ROLE_OBSERVER) {
            // observers don't count towards the offering's seats filled
            return -1
          }
          return offering.seats_available
        }
        return Math.max(
          offering.resource_license.seat_count -
            offering.resource_license.seats_filled,
          0
        )
      },

      getRegistrationContext() {
        let regContext = null
        const registration = this.get("registration")
        if (registration) {
          regContext = registration.toJSON()
        }
        return regContext
      },

      resourceIs(type) {
        const resource_type = this.get("resource")?.get("resource_type")

        switch (resource_type) {
          case 10:
            return type === "course"
          case 20:
            return type === "lesson"
        }
      },

      resourceUrl() {
        const { resource, offering, section } = this.getState()
        let url = `/library/${resource.slug}/`
        if (offering) {
          url = `${url}${offering.id}/`
        }
        url = `${url}${section}/`
        return url
      }
    }
  }

  defaults() {
    return {
      resource: null, // The resource being viewed
      offerings: null, // The offerings attached to the resource
      offering: null, // The offering being viewed
      registration: null, // The user's registration if any
      section: "about", // Which tab is the user looking at?
      url: null // the url matching the current state
    }
  }

  _getResourceLoadEpoch() {
    if (this._resourceLoadEpoch == null) {
      this._resourceLoadEpoch = 0
    }
    return this._resourceLoadEpoch
  }

  _beginResourceLoadEpoch() {
    this._resourceLoadEpoch = this._getResourceLoadEpoch() + 1
    return this._resourceLoadEpoch
  }

  _invalidateResourceLoadEpoch() {
    this._resourceLoadEpoch = this._getResourceLoadEpoch() + 1
  }

  _isCurrentResourceLoadEpoch(epoch) {
    return epoch == null || epoch === this._getResourceLoadEpoch()
  }

  _assertResourceLoadEpoch(epoch) {
    if (!this._isCurrentResourceLoadEpoch(epoch)) {
      throw new DOMException("Resource load superseded", "AbortError")
    }
  }

  computeURLForState() {
    ;`\
Returns a URL matching the user's existing state\
`
    const { resource, offering, section } = this.getState()
    let url = `/library/${resource.slug}/`
    if (offering) {
      url = `${url}${offering.id}/`
    }
    url = `${url}${section}/`
    return url
  }

  bootstrapStore() {
    if (!window.bootstrappedData) {
      return
    }
    const data = {}
    const { resource, offering } = window.bootstrappedData
    if (resource) {
      data.resource = new Resource(bootstrappedData.resource)
    }
    if (offering) {
      data.offering = new Offering(bootstrappedData.offering)
    }
    if (!_.isEmpty(data)) {
      return this._mutate(data)
    }
  }
}
ResourceStore.initClass()

export default window.App.stores.registerStore("resource", ResourceStore)
